/**
 * Token Usage Chart Component
 * Displays token usage over time as a stacked bar chart using recharts
 */
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, BarChart3 } from 'lucide-react'
import { formatCompactNumber } from '@/lib/utils'
import { chartColors, chartDefaults } from '@/components/charts/chartConfig'
import type { TokenTimeSeriesDataPoint } from '@/types/chatbot'

export interface TokenUsageChartProps {
  /** Time series data points */
  data: TokenTimeSeriesDataPoint[]
  /** Whether data is loading */
  isLoading?: boolean
  /** Error message if fetch failed */
  error?: string
  /** Chart title */
  title?: string
  /** Height of the chart in pixels */
  height?: number
  /** Granularity for x-axis formatting */
  granularity?: 'day' | 'hour'
}

/**
 * Format date based on granularity
 */
function formatDate(dateStr: string, granularity: 'day' | 'hour'): string {
  const date = new Date(dateStr)
  if (granularity === 'hour') {
    return date.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      hour12: true
    })
  }
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric' 
  })
}

/**
 * Custom tooltip for the chart
 */
function CustomTooltip({ 
  active, 
  payload, 
  label 
}: {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
}) {
  if (!active || !payload || !payload.length) return null

  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0)

  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 min-w-[200px]">
      <p className="font-medium mb-2">{label}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex justify-between text-sm py-0.5">
          <span style={{ color: entry.color }}>{entry.name}</span>
          <span className="font-mono ml-4">{formatCompactNumber(entry.value)}</span>
        </div>
      ))}
      <div className="border-t mt-2 pt-2 flex justify-between font-medium">
        <span>Total</span>
        <span className="font-mono">{formatCompactNumber(total)}</span>
      </div>
    </div>
  )
}

/**
 * Token Usage Chart - Stacked bar chart showing token usage over time
 * Shows input/output tokens breakdown
 */
export function TokenUsageChart({
  data,
  isLoading = false,
  error,
  title = 'Token Usage Over Time',
  height = 400,
  granularity = 'day'
}: TokenUsageChartProps) {
  // Transform data for chart - now using input/output breakdown
  const chartData = data.map(point => ({
    date: formatDate(point.date, granularity),
    'Input Tokens': point.inputTokens,
    'Output Tokens': point.outputTokens,
    sessions: point.sessionCount,
  }))

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="w-full" style={{ height }} />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center text-muted-foreground" style={{ height }}>
            <AlertCircle className="h-12 w-12 mb-2 text-destructive" />
            <p>Failed to load chart data</p>
            <p className="text-sm">{error}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center text-muted-foreground" style={{ height }}>
            <BarChart3 className="h-12 w-12 mb-2 opacity-50" />
            <p>No token usage data available</p>
            <p className="text-sm">Run some sessions to start tracking tokens</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={height} minHeight={1}>
          <BarChart data={chartData} margin={chartDefaults.margin}>
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={chartColors.grid}
              opacity={0.5}
            />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12, fill: chartColors.muted }}
              tickLine={false}
              axisLine={{ stroke: chartColors.grid }}
            />
            <YAxis 
              tickFormatter={(value) => formatCompactNumber(value)}
              tick={{ fontSize: 12, fill: chartColors.muted }}
              tickLine={false}
              axisLine={{ stroke: chartColors.grid }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
            />
            {/* Input tokens - muted slate/purple for large volume */}
            <Bar 
              dataKey="Input Tokens" 
              stackId="a" 
              fill={chartColors.tokenInput}
              name="Input Tokens"
              radius={[0, 0, 0, 0]}
            />
            {/* Output tokens - accent color for smaller volume */}
            <Bar 
              dataKey="Output Tokens" 
              stackId="a" 
              fill={chartColors.tokenOutput}
              name="Output Tokens"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}
